/*
 * Tipos do wire do historico de alertas (FE-7 — Tela 7 §10.5).
 *
 * Espelham FIELMENTE os schemas Pydantic do backend (`flow/api/alertas/rotas.py`):
 * `AlertaHistoricoOut`, `EmpresaAlertasOut` e a resposta de
 * `POST /api/outbox/{id}/reagendar`.
 *
 * RIGOR FINANCEIRO: `saldo_observado` e `NUMERIC(18,2)` no banco — chega como
 * STRING no wire e NUNCA e convertido para `number` nesta camada (a formatacao
 * usa `formatarDecimal`, string-based). Datas/datetimes sao ISO e formatados pelos
 * helpers string-based de `lib/formato/data` (timezone-safe).
 *
 * Vocabularios (severidade/canal/status) sao tuplas `as const` espelhando os
 * enums do backend — fonte unica do par valor->rotulo para os filtros e a tabela.
 */

/**
 * Uma linha do historico de alertas (`GET /api/alertas`).
 *
 * Grao: uma notificacao do outbox de um alerta, com o seu canal e status. A
 * resposta e ARRAY PURO — sem envelope/total; a paginacao e por `limit`/`offset`
 * na query (ver `FiltrosAlertas`).
 *
 * SEM PII de canal: NAO ha `payload`/`destinatario` (outbox) nem `detalhe`
 * (entrega). O unico campo de erro e `ultimo_erro` (mensagem categorica ja
 * sanitizada na origem pelo despachante). `empresa_id === null` => alerta de GRUPO.
 */
export interface AlertaHistorico {
  id_outbox: string;
  alerta_id: string;
  regra_id: string;
  empresa_id: string | null;
  severidade: string;
  data_referencia: string;
  saldo_observado: string;
  canal: string;
  status: string;
  tentativas: number;
  proximo_envio_em: string;
  falhou_em: string | null;
  ultimo_erro: string | null;
  disparado_em: string;
}

/**
 * Item de `GET /api/alertas/empresas` — empresa configuravel pelo usuario
 * (escopo `configurar_alertas`).
 *
 * Lista de selecao do dropdown de filtro de empresa da tela. Pode incluir
 * empresas INATIVAS (`ativa === false`) — a UI rotula com `fantasia ?? razao_social`.
 * A lista pode vir VAZIA (`[]`) quando o escopo do usuario nao inclui empresa
 * alguma.
 */
export interface EmpresaAlertas {
  id: string;
  razao_social: string;
  fantasia: string | null;
  cnpj: string;
  ativa: boolean;
}

/**
 * Resposta de `POST /api/outbox/{outbox_id}/reagendar`.
 *
 * O backend reabre a notificacao para novo envio e devolve o estado atualizado.
 */
export interface ReagendarResposta {
  id: string;
  status: string;
  tentativas: number;
  proximo_envio_em: string;
}

/**
 * Filtros da consulta do historico (query params do `GET /api/alertas`).
 *
 * `limit`/`offset` sao SEMPRE enviados (tem default no backend e nesta camada). Os
 * demais sao OPCIONAIS: a camada de `api` OMITE do objeto `params` toda chave
 * opcional ausente ou vazia (nunca envia `severidade=""`). `empresa_id` e um UUID
 * string; `severidade`/`canal`/`status` sao valores das tuplas abaixo (o backend
 * valida `status` contra o enum — 422 se invalido).
 */
export interface FiltrosAlertas {
  limit: number;
  offset: number;
  empresa_id?: string;
  severidade?: string;
  canal?: string;
  status?: string;
}

/**
 * Severidades do alerta — espelham o StrEnum `Severidade` do backend. Tupla
 * `as const` (readonly) de pares [valor lowercase do enum, rotulo pt-BR].
 */
export const SEVERIDADES = [
  ["informativa", "Informativa"],
  ["baixa", "Baixa"],
  ["media", "Média"],
  ["alta", "Alta"],
  ["critica", "Crítica"],
] as const;

/**
 * Canais de notificacao — espelham os tipos de canal do backend. Tupla `as const`
 * de pares [valor lowercase, rotulo pt-BR].
 */
export const CANAIS = [
  ["smtp", "SMTP"],
  ["teams", "Teams"],
  ["slack", "Slack"],
  ["whatsapp", "WhatsApp"],
] as const;

/**
 * Status do outbox de notificacao — espelham o StrEnum `StatusOutboxNotificacao`
 * do backend. Tupla `as const` de pares [valor lowercase, rotulo pt-BR].
 */
export const STATUSES = [
  ["pendente", "Pendente"],
  ["em_envio", "Em envio"],
  ["enviado", "Enviado"],
  ["reagendado", "Reagendado"],
  ["dead_letter", "Dead letter"],
] as const;

/** Limite padrao de itens por pagina — espelha o default do backend. */
export const LIMITE_DEFAULT = 50;
